import cx from "classnames"
import PropTypes from "prop-types"
import React from "react"

function Heading({
  className,
  level = "h2",
  decoration = "none",
  label,
  title,
  subtitle,
  size = "regular",
  inverted,
  narrow,
}) {
  const Tag = level || "h2"

  const renderTitle = () => (
    <Tag
      className={cx(
        "mt-0 font-bold leading-tighter md:leading-tighter",
        inverted ? "text-white" : "text-black",
        {
          "text-5xl md:text-7xl": size === "huge",
          "text-4xl md:text-6xl": size === "large",
          "text-4xl md:text-5xl": size === "regular",
        }
      )}
    >
      {title}
    </Tag>
  )

  const renderSubtitle = () => {
    if (subtitle) {
      return (
        <div
          className={cx(
            "mt-4 text-xl md:mt-3 md:text-2xl",
            inverted ? "text-sage" : "text-dark-grey",
            narrow ? "max-w-2xl" : "max-w-4xl"
          )}
        >
          {subtitle}
        </div>
      )
    }
    return null
  }

  const renderLineLayout = () => (
    <>
      {label && (
        <div className="relative mb-2 text-base font-bold text-lush md:text-lg">
          <hr className="absolute -left-7 top-1/2 hidden h-px w-10 -translate-x-full border-0 bg-lush lg:block" />
          {label}
        </div>
      )}
      {renderTitle()}
      {renderSubtitle()}
    </>
  )

  const renderBorderedLayout = () => (
    <>
      <div
        className={cx("w-full", {
          "lg:-ml-8 lg:border-l lg:border-lush lg:pl-8": !!label,
        })}
      >
        {label && (
          <div className="relative mb-2 text-base font-bold text-lush md:text-lg">
            {label}
          </div>
        )}
        {renderTitle()}
      </div>
      {renderSubtitle()}
    </>
  )

  const renderPlainLayout = () => (
    <>
      {label && (
        <div className="mb-2 text-base font-bold text-lush md:text-lg">
          {label}
        </div>
      )}
      {renderTitle()}
      {renderSubtitle()}
    </>
  )

  return (
    <div className={className}>
      {(() => {
        switch (decoration) {
          case "line":
            return renderLineLayout()
          case "border":
            return renderBorderedLayout()
          default:
            return renderPlainLayout()
        }
      })()}
    </div>
  )
}

Heading.propTypes = {
  /** Additional className(s) */
  className: PropTypes.string,
  /** Used to specify the header level for SEO / styling purposes */
  level: PropTypes.oneOf(["h1", "h2", "h3"]),
  /** Small text that appears above the title */
  label: PropTypes.string,
  /** Title */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** Subtitle */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Title size (72px, 60px, 48px) */
  size: PropTypes.oneOf(["huge", "large", "regular"]),
  /** Adds a line to the label ("line"); Adds a left border ("border"); No decoration ("none"); */
  decoration: PropTypes.oneOf(["line", "border", "none"]),
  /** If true, change text to light colors */
  inverted: PropTypes.bool,
  /** If true, narrows subtitle width */
  narrow: PropTypes.bool,
}

export default Heading
